import { createInertiaApp, router } from '@inertiajs/react'
import { createRoot } from 'react-dom/client'
import type { ComponentType, ReactNode } from 'react'
import axios from 'axios'
import AppLayout from '../layouts/AppLayout'
import { PomodoroProvider } from '../context/PomodoroContext'
import AntdTheme from '../components/AntdTheme'
import AgentMount from '../components/Agent/AgentMount'
import InputCenterMount from '../components/InputCenter/InputCenterMount'
import { applyFavicon } from '../utils/favicon'
// The three brand fonts, bundled and served from our own origin rather than
// Google Fonts (see test/self_hosted_fonts_test.rb for why). Outfit and
// Schibsted Grotesk are variable fonts: one file covers every weight. Plex
// Mono only sets timestamps, slugs and task types, so latin 400/500 is all of
// it. Chinese text never used these and still falls back to PingFang SC.
import '@fontsource-variable/outfit'
import '@fontsource-variable/schibsted-grotesk'
import '@fontsource/ibm-plex-mono/latin-400.css'
import '@fontsource/ibm-plex-mono/latin-500.css'
import '../styles/application.css'

// Rails' forgery protection validates the X-CSRF-Token header. Inertia uses the
// default axios instance; point it at the XSRF-TOKEN cookie set by
// Web::Base#set_csrf_cookie and send it under the header Rails expects.
axios.defaults.xsrfCookieName = 'XSRF-TOKEN'
axios.defaults.xsrfHeaderName = 'X-CSRF-Token'

// Swap the tab icon when navigation crosses sections (utils/favicon.ts). Both
// events are needed: navigate covers link visits and back/forward but skips
// replace visits; success covers every successful visit but not back/forward.
// When both fire for one page the second call is a no-op.
router.on('navigate', (event) => applyFavicon(event.detail.page.props.favicon))
router.on('success', (event) => applyFavicon(event.detail.page.props.favicon))

type PageComponent = ComponentType & {
  layout?: (page: ReactNode) => ReactNode
  __timerWrapped?: boolean
}
type PageModule = { default: PageComponent }

createInertiaApp({
  // A page's <Head title> wins; a page without one keeps the app name. Without
  // this fallback Inertia would drop the server-rendered <title inertia> on
  // pages that render no <Head title>, leaving the tab with only the URL. No
  // suffix here: public pages get their full title from the server
  // (inertia_meta), and a suffix would be appended to it a second time.
  title: (title) => title || 'asaptodo',
  // Resolve page components from app/frontend/pages/**, wrapping each in the
  // app shell (Header/Footer) by default — mirrors the old Next.js root layout.
  //
  // Lazy, one chunk per page. With `eager: true` every page — calendar,
  // charts, drag-and-drop, markdown, admin — was in the entry chunk
  // (2.65 MB), and the logged-out landing page had to download and parse all
  // of it before rendering anything.
  resolve: async (name) => {
    const pages = import.meta.glob<PageModule>('../pages/**/*.tsx')
    const importPage = pages[`../pages/${name}.tsx`]
    if (!importPage) {
      throw new Error(`Inertia page not found: ${name} (app/frontend/pages/${name}.tsx)`)
    }
    const page = await importPage()

    // 每个页面的 layout 外面再套一层 PomodoroProvider —— 番茄钟要在换页之后
    // 继续走，而不是一离开 /pomodoro 就停。
    //
    // 为什么套在 layout 外面、而不是放进 AppLayout：TodoLayout / WikiLayout /
    // AdminLayout 都不复用 AppLayout，放进去的话从番茄钟走到 /todo 照样断，
    // 而那正是人在一个番茄钟里最常去的地方。
    //
    // 为什么这样就不会被卸载：每个页面返回的元素树最外层都是同一个
    // PomodoroProvider 组件类型，位置也相同，React 换页时按类型复用这个实例，
    // 只换它的 children。Inertia 的 key 挂在页面组件上，挂不到这一层。
    //
    // __timerWrapped：import.meta.glob 拿到的模块是缓存的，同一页第二次访问
    // 还会进这里，不挡一下就会一层层往上套。
    if (!page.default.__timerWrapped) {
      const inner = page.default.layout ?? ((p: ReactNode) => <AppLayout>{p}</AppLayout>)
      page.default.layout = (p: ReactNode) => <PomodoroProvider>{inner(p)}</PomodoroProvider>
      page.default.__timerWrapped = true
    }
    return page
  },
  setup({ el, App, props }) {
    // The server's plain-HTML copy for crawlers (app/views/inertia.html.erb)
    // has done its job once React is about to render the real page.
    document.querySelectorAll('[data-prerender]').forEach((node) => node.remove())

    const signedIn = Boolean((props.initialPage.props as { auth?: { user?: unknown } }).auth?.user)

    // antd 的组件此前跑在它自己的默认浅色主题上（整个项目一个 ConfigProvider
    // 都没有）。包在最外层而不是逐页包：24 个文件用到 antd，逐页包必然漏。
    createRoot(el).render(
      <AntdTheme>
        <App {...props} />
        <AgentMount signedIn={signedIn} />
        <InputCenterMount signedIn={signedIn} />
      </AntdTheme>,
    )
  },
})
